import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from 'react';
import {
  Check,
  Eye,
  Grid2X2,
  Pencil,
  Plus,
  Printer,
  RotateCcw,
  Save,
  Search,
  Send,
  SlidersHorizontal,
  Table2,
  Trash2,
  Truck,
  X,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/Components/ui/Button';
import { Card, CardHead } from '@/Components/ui/Card';
import { Input } from '@/Components/ui/Input';
import { Select } from '@/Components/ui/Select';
import { Textarea } from '@/Components/ui/Textarea';
import { IconButton } from '@/Components/ui/IconButton';
import { PageHeader } from '@/Components/data/PageHeader';
import { EmptyState } from '@/Components/data/EmptyState';
import { CountBadge, StatusBadge } from '@/Components/ui/Badge';
import { FilterBar, FilterField } from '@/Components/data/FilterBar';
import { SearchField } from '@/Components/forms/SearchField';
import type { Tone } from '@/Components/ui/types';
import DecimalInput from '@/Components/forms/DecimalInput';
import { Field } from '@/Components/forms/Field';
import { DataTableColumnsMenu } from '@/Components/data/DataTableColumnsMenu';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from '@/Components/ui/DropdownMenu';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/Components/ui/Dialog';
import romaneioService, { type Romaneio, type RomaneioItem, type RomaneioStatus } from '@/services/romaneio';
import veiculoService, { type Veiculo } from '@/services/veiculo';
import responsavelService, { type Responsavel } from '@/services/responsavel';
import produtoService, { type Produto } from '@/services/produto';
import loteService, { type Lote } from '@/services/lote';
import { getUuidCandidate, isUuid } from '@/utils/uuid';

const STATUS_LABELS: Record<RomaneioStatus, string> = {
  ABERTO: 'Aberto',
  CARREGADO: 'Carregado',
  EM_TRANSITO: 'Em Trânsito',
  ENTREGUE: 'Entregue',
  CANCELADO: 'Cancelado',
};
const STATUS_TONES: Record<RomaneioStatus, Tone> = {
  ABERTO: 'info',
  CARREGADO: 'warning',
  EM_TRANSITO: 'info',
  ENTREGUE: 'success',
  CANCELADO: 'danger',
};

type RomaneioColumnId = 'numero' | 'destinatario' | 'status' | 'veiculo' | 'motorista' | 'emissao';

const romaneioColumns: { id: RomaneioColumnId; label: string }[] = [
  { id: 'numero', label: 'Número' },
  { id: 'destinatario', label: 'Destinatário' },
  { id: 'status', label: 'Status' },
  { id: 'veiculo', label: 'Veículo' },
  { id: 'motorista', label: 'Motorista' },
  { id: 'emissao', label: 'Data Emissão' },
];

function formatDate(v?: string | null) {
  if (!v) return '-';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return v;
  return d.toLocaleDateString('pt-BR');
}

function renderRomaneioColumn(item: Romaneio, columnId: RomaneioColumnId): ReactNode {
  if (columnId === 'numero') return item.numero;
  if (columnId === 'destinatario') return item.destinatario_nome || '-';
  if (columnId === 'status') return <StatusBadge label={STATUS_LABELS[item.status]} tone={STATUS_TONES[item.status]} />;
  if (columnId === 'veiculo') return item.veiculo_placa || '-';
  if (columnId === 'motorista') return item.motorista || '-';
  return formatDate(item.data_emissao);
}

const ITEM_VAZIO: RomaneioItem = { produto_id: '', lote_id: '', quantidade: 0, observacoes: '' };

const FORM_VAZIO = {
  numero: '',
  responsavel_emissao_id: '',
  destinatario_nome: '',
  destinatario_cnpj: '',
  destinatario_endereco: '',
  transportadora: '',
  motorista: '',
  veiculo_id: '',
  observacoes: '',
  itens: [] as RomaneioItem[],
};

export default function RomaneioPage() {
  const navigate = useNavigate();
  const [romaneios, setRomaneios] = useState<Romaneio[]>([]);
  const [veiculos, setVeiculos] = useState<Veiculo[]>([]);
  const [responsaveis, setResponsaveis] = useState<Responsavel[]>([]);
  const [produtos, setProdutos] = useState<Produto[]>([]);
  const [lotesPorProduto, setLotesPorProduto] = useState<Record<number, Lote[]>>({});
  const [loading, setLoading] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [processando, setProcessando] = useState<string | null>(null);

  const [filtroStatus, setFiltroStatus] = useState<RomaneioStatus | null>(null);
  const [draftFiltroStatus, setDraftFiltroStatus] = useState<RomaneioStatus | null>(null);
  const [busca, setBusca] = useState('');
  const [draftBusca, setDraftBusca] = useState('');
  const [filtroVeiculo, setFiltroVeiculo] = useState('');
  const [draftFiltroVeiculo, setDraftFiltroVeiculo] = useState('');
  const [filtroMotorista, setFiltroMotorista] = useState('');
  const [draftFiltroMotorista, setDraftFiltroMotorista] = useState('');
  const [perPage, setPerPage] = useState(20);
  const [page, setPage] = useState(1);
  const [viewMode, setViewMode] = useState<'table' | 'grid'>('table');
  const [compactActions, setCompactActions] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState<Record<RomaneioColumnId, boolean>>(
    () => Object.fromEntries(romaneioColumns.map((column) => [column.id, true])) as Record<RomaneioColumnId, boolean>,
  );
  const [columnOrder, setColumnOrder] = useState<RomaneioColumnId[]>(() => romaneioColumns.map((column) => column.id));

  const [dialog, setDialog] = useState(false);
  const [form, setForm] = useState(FORM_VAZIO);

  const [dialogDetalhes, setDialogDetalhes] = useState(false);
  const [itemVisualizar, setItemVisualizar] = useState<Romaneio | null>(null);

  const [dialogCarregar, setDialogCarregar] = useState(false);
  const [romaneioAcao, setRomaneioAcao] = useState<Romaneio | null>(null);
  const [responsavelCarregamento, setResponsavelCarregamento] = useState('');

  const [dialogImprimirIndisponivel, setDialogImprimirIndisponivel] = useState(false);
  const veiculosValidos = useMemo(() => veiculos.filter((veiculo) => getUuidCandidate(veiculo)), [veiculos]);
  const responsaveisValidos = useMemo(() => responsaveis.filter((responsavel) => getUuidCandidate(responsavel)), [responsaveis]);
  const produtosValidos = useMemo(() => produtos.filter((produto) => getUuidCandidate(produto)), [produtos]);

  const carregar = async () => {
    setLoading(true);
    try {
      setRomaneios(await romaneioService.listar({
        status: filtroStatus || undefined,
        veiculo: filtroVeiculo || undefined,
        search: busca || undefined,
        motorista: filtroMotorista || undefined,
      }));
    } catch (error) {
      console.error('Erro ao carregar romaneios', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    veiculoService.listar().then(setVeiculos).catch(console.error);
    responsavelService.listar().then(setResponsaveis).catch(console.error);
    produtoService.listar().then(setProdutos).catch(console.error);
  }, []);

  useEffect(() => {
    carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtroStatus, filtroVeiculo, busca, filtroMotorista]);

  const romaneiosFiltrados = useMemo(() => {
    return romaneios;
  }, [romaneios]);
  const lastPage = Math.max(1, Math.ceil(romaneiosFiltrados.length / perPage));
  const currentPage = Math.min(page, lastPage);
  const pageData = useMemo(() => {
    const start = (currentPage - 1) * perPage;
    return romaneiosFiltrados.slice(start, start + perPage);
  }, [currentPage, perPage, romaneiosFiltrados]);
  const activeColumns = useMemo(
    () => columnOrder
      .map((id) => romaneioColumns.find((column) => column.id === id))
      .filter((column): column is (typeof romaneioColumns)[number] => Boolean(column && visibleColumns[column.id])),
    [columnOrder, visibleColumns],
  );
  const from = romaneiosFiltrados.length === 0 ? 0 : (currentPage - 1) * perPage + 1;
  const to = romaneiosFiltrados.length === 0 ? 0 : Math.min(from + pageData.length - 1, romaneiosFiltrados.length);

  const submitFilters = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setPage(1);
    setBusca(draftBusca.trim());
    setFiltroStatus(draftFiltroStatus);
    setFiltroVeiculo(draftFiltroVeiculo);
    setFiltroMotorista(draftFiltroMotorista.trim());
  };

  const clearFilters = () => {
    setDraftBusca('');
    setBusca('');
    setDraftFiltroStatus(null);
    setFiltroStatus(null);
    setDraftFiltroVeiculo('');
    setFiltroVeiculo('');
    setDraftFiltroMotorista('');
    setFiltroMotorista('');
    setPage(1);
  };

  const abrirDialog = () => {
    setForm({ ...FORM_VAZIO, numero: `ROM-${Date.now().toString().slice(-8)}` });
    setLotesPorProduto({});
    setDialog(true);
  };

  const adicionarItem = () => {
    setForm({ ...form, itens: [...form.itens, { ...ITEM_VAZIO }] });
  };

  const removerItem = (index: number) => {
    setForm({ ...form, itens: form.itens.filter((_, i) => i !== index) });
  };

  const onProdutoItemChange = async (index: number, produtoId: string) => {
    const novos = [...form.itens];
    novos[index] = { ...novos[index], produto_id: produtoId, lote_id: '' };
    setForm({ ...form, itens: novos });
    if (!produtoId) return;
    try {
      const lotes = await loteService.listar({ produto: produtoId, status: 'LIVRE' });
      setLotesPorProduto((prev) => ({ ...prev, [index]: lotes }));
    } catch (error) {
      console.error('Erro ao carregar lotes do produto', error);
    }
  };

  const atualizarItem = (index: number, patch: Partial<RomaneioItem>) => {
    const novos = [...form.itens];
    novos[index] = { ...novos[index], ...patch };
    setForm({ ...form, itens: novos });
  };

  const salvar = async () => {
    if (!form.numero || !isUuid(form.responsavel_emissao_id) || !form.destinatario_nome || !form.destinatario_endereco || form.itens.length === 0) return;
    setSalvando(true);
    try {
      await romaneioService.criar({
        numero: form.numero,
        responsavel_emissao_id: form.responsavel_emissao_id,
        destinatario_nome: form.destinatario_nome,
        destinatario_cnpj: form.destinatario_cnpj || null,
        destinatario_endereco: form.destinatario_endereco,
        transportadora: form.transportadora || null,
        motorista: form.motorista || null,
        veiculo_id: form.veiculo_id || null,
        observacoes: form.observacoes || null,
        itens: form.itens,
      });
      setDialog(false);
      await carregar();
    } catch (error) {
      console.error('Erro ao salvar romaneio', error);
    } finally {
      setSalvando(false);
    }
  };

  const visualizar = (item: Romaneio) => {
    setItemVisualizar(item);
    setDialogDetalhes(true);
  };

  const imprimir = async (item: Romaneio) => {
    try {
      await romaneioService.imprimir(item.id);
    } catch {
      // Backend ainda nao implementa geracao de PDF (RomaneioController::imprimir retorna 501)
    } finally {
      setDialogImprimirIndisponivel(true);
    }
  };

  const abrirCarregar = (item: Romaneio) => {
    setRomaneioAcao(item);
    setResponsavelCarregamento('');
    setDialogCarregar(true);
  };

  const confirmarCarregar = async () => {
    if (!romaneioAcao || !responsavelCarregamento) return;
    setProcessando(romaneioAcao.id);
    try {
      if (!isUuid(responsavelCarregamento)) return;
      await romaneioService.carregar(romaneioAcao.id, responsavelCarregamento);
      setDialogCarregar(false);
      await carregar();
    } catch (error) {
      console.error('Erro ao carregar romaneio', error);
    } finally {
      setProcessando(null);
    }
  };

  const expedir = async (item: Romaneio) => {
    setProcessando(item.id);
    try {
      await romaneioService.expedir(item.id);
      await carregar();
    } catch (error) {
      console.error('Erro ao expedir romaneio', error);
    } finally {
      setProcessando(null);
    }
  };

  const entregar = async (item: Romaneio) => {
    setProcessando(item.id);
    try {
      await romaneioService.entregar(item.id);
      await carregar();
    } catch (error) {
      console.error('Erro ao marcar romaneio como entregue', error);
    } finally {
      setProcessando(null);
    }
  };

  const cancelar = async (item: Romaneio) => {
    setProcessando(item.id);
    try {
      await romaneioService.cancelar(item.id);
      await carregar();
    } catch (error) {
      console.error('Erro ao cancelar romaneio', error);
    } finally {
      setProcessando(null);
    }
  };

  return (
    <div className="nx-page">
      <PageHeader
        icon={<Truck size={21} strokeWidth={2} />}
        title="Romaneios de Entrega"
        description="Emissão e acompanhamento dos romaneios de expedição e carregamento."
        actions={
          <Button type="button" onClick={() => navigate('/producao/romaneios/novo')}>
            <Plus size={16} strokeWidth={2.2} />
            Novo Romaneio
          </Button>
        }
      />

      <Card className="nx-page-card nx-sngpc-listing-card">
        <CardHead
          title="Listagem"
          actions={
            <div className="nx-card-head-actions nx-listing-toolbar">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button type="button" variant="ghost"><Grid2X2 size={16} /> Visualização</Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuLabel>Tipo de visualização</DropdownMenuLabel>
                  <DropdownMenuRadioGroup value={viewMode} onValueChange={(value) => setViewMode(value as 'table' | 'grid')}>
                    <DropdownMenuRadioItem value="table"><Table2 size={15} /> Tabela</DropdownMenuRadioItem>
                    <DropdownMenuRadioItem value="grid"><Grid2X2 size={15} /> Grade</DropdownMenuRadioItem>
                  </DropdownMenuRadioGroup>
                </DropdownMenuContent>
              </DropdownMenu>
              <DataTableColumnsMenu
                options={romaneioColumns}
                columnOrder={columnOrder}
                visibility={visibleColumns}
                onToggle={(id, visible) => setVisibleColumns((current) => ({ ...current, [id]: visible }))}
                onReorder={(order) => setColumnOrder([...order])}
                onReset={() => {
                  setVisibleColumns(Object.fromEntries(romaneioColumns.map((column) => [column.id, true])) as Record<RomaneioColumnId, boolean>);
                  setColumnOrder(romaneioColumns.map((column) => column.id));
                }}
              />
              <Button type="button" variant="ghost" aria-pressed={compactActions} onClick={() => setCompactActions((value) => !value)}>
                <SlidersHorizontal size={15} />
                Modo de Ações
              </Button>
              <CountBadge count={`${romaneiosFiltrados.length} registros`} />
            </div>
          }
        />

        <FilterBar
          onSubmit={submitFilters}
          search={
            <SearchField
              label="Buscar por número, destinatário, CNPJ, transportadora ou placa"
              placeholder="Buscar romaneio..."
              value={draftBusca}
              onChange={(event) => setDraftBusca(event.target.value)}
            />
          }
          filters={
            <>
              <FilterField label="Status" className="nx-sngpc-filter-field">
                <Select filter value={draftFiltroStatus ?? ''} onChange={(event) => setDraftFiltroStatus((event.target.value || null) as RomaneioStatus | null)}>
                  <option value="">Todos</option>
                  {Object.entries(STATUS_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </Select>
              </FilterField>
              <FilterField label="Veículo" className="nx-sngpc-filter-field">
                <Select filter value={draftFiltroVeiculo} onChange={(event) => setDraftFiltroVeiculo(event.target.value)}>
                  <option value="">Todos</option>
                  {veiculosValidos.map((v) => (
                    <option key={v.id} value={getUuidCandidate(v) || ''}>{v.placa}</option>
                  ))}
                </Select>
              </FilterField>
              <FilterField label="Motorista" className="nx-sngpc-filter-field">
                <Input value={draftFiltroMotorista} onChange={(event) => setDraftFiltroMotorista(event.target.value)} placeholder="Motorista..." />
              </FilterField>
              <FilterField label="Por página">
                <Select filter value={perPage} onChange={(event) => { setPage(1); setPerPage(Number(event.target.value)); }}>
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </Select>
              </FilterField>
            </>
          }
          actions={<Button type="button" variant="ghost" onClick={clearFilters}><RotateCcw size={15} /> Limpar</Button>}
        />

        {!loading && romaneiosFiltrados.length === 0 ? (
          <EmptyState message="Nenhum romaneio encontrado." />
        ) : viewMode === 'grid' ? (
          <div className="nx-grid-3">
            {pageData.map((item) => (
              <Card key={item.id} className="nx-sngpc-item-card">
                <div className="nx-sngpc-item-card__header">
                  <div>
                    <strong>{item.numero}</strong>
                    <span>{item.destinatario_nome || '-'}</span>
                  </div>
                  <StatusBadge label={STATUS_LABELS[item.status]} tone={STATUS_TONES[item.status]} />
                </div>
                <div className="nx-sngpc-item-card__body">
                  <span>{item.veiculo_placa || 'Sem veículo'}</span>
                  <span>{item.motorista || 'Sem motorista'}</span>
                  <span>{formatDate(item.data_emissao)}</span>
                </div>
                <div className="nx-table-actions-group nx-table-actions-group--buttons">
                  <IconButton label="Visualizar" onClick={() => navigate(`/producao/romaneios/${item.id}/visualizar`)}><Eye size={15} strokeWidth={1.9} aria-hidden /></IconButton>
                  <IconButton label="Imprimir" onClick={() => imprimir(item)}><Printer size={15} strokeWidth={1.9} aria-hidden /></IconButton>
                </div>
              </Card>
            ))}
          </div>
        ) : (
          <div className="nx-table-wrap">
            <table className="nx-table">
              <thead>
                <tr>
                  {activeColumns.map((column) => <th key={column.id}>{column.label}</th>)}
                  <th className="nx-col-acoes">Ações</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={activeColumns.length + 1} className="nx-table-loading">Carregando…</td>
                  </tr>
                ) : (
                  pageData.map((item) => (
                    <tr key={item.id}>
                      {activeColumns.map((column) => <td key={column.id}>{renderRomaneioColumn(item, column.id)}</td>)}
                      <td className="nx-col-acoes">
                        <div className="nx-table-actions-group nx-table-actions-group--buttons">
                          <IconButton label="Visualizar" onClick={() => navigate(`/producao/romaneios/${item.id}/visualizar`)}>
                            <Eye size={15} strokeWidth={1.9} aria-hidden />
                          </IconButton>
                          <IconButton label="Imprimir" onClick={() => imprimir(item)}>
                            <Printer size={15} strokeWidth={1.9} aria-hidden />
                          </IconButton>
                          {item.status === 'ABERTO' && (
                            <IconButton
                              label="Carregar"
                              onClick={() => abrirCarregar(item)}
                              disabled={processando === item.id}
                            >
                              <Truck size={15} strokeWidth={1.9} aria-hidden />
                            </IconButton>
                          )}
                          {item.status === 'CARREGADO' && (
                            <IconButton
                              label="Expedir"
                              onClick={() => expedir(item)}
                              disabled={processando === item.id}
                            >
                              <Send size={15} strokeWidth={1.9} aria-hidden />
                            </IconButton>
                          )}
                          {item.status === 'EM_TRANSITO' && (
                            <IconButton
                              label="Entregar"
                              onClick={() => entregar(item)}
                              disabled={processando === item.id}
                            >
                              <Check size={15} strokeWidth={1.9} aria-hidden />
                            </IconButton>
                          )}
                          {['ABERTO', 'CARREGADO'].includes(item.status) && (
                            <IconButton
                              label="Cancelar"
                              variant="danger"
                              onClick={() => cancelar(item)}
                              disabled={processando === item.id}
                            >
                              <X size={15} strokeWidth={1.9} aria-hidden />
                            </IconButton>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
        <div className="nx-table-footer">
          <span>{romaneiosFiltrados.length === 0 ? '0 registros' : `${from}-${to} de ${romaneiosFiltrados.length} registros`}</span>
          <div className="nx-table-pagination">
            <Button type="button" variant="ghost" onClick={() => setPage((value) => Math.max(1, value - 1))} disabled={currentPage <= 1}>Anterior</Button>
            <span>{currentPage} / {lastPage}</span>
            <Button type="button" variant="ghost" onClick={() => setPage((value) => Math.min(lastPage, value + 1))} disabled={currentPage >= lastPage}>Próxima</Button>
          </div>
        </div>
      </Card>

      <Dialog open={dialog} onOpenChange={(next) => !salvando && setDialog(next)}>
        <DialogContent className="nexus-dialog--wide nx-sngpc-edit-dialog">
          <form onSubmit={(event) => { event.preventDefault(); void salvar(); }}>
          <DialogHeader>
            <DialogTitle>Novo Romaneio</DialogTitle>
            <DialogDescription>Preencha os dados para emitir um novo romaneio de expedição.</DialogDescription>
          </DialogHeader>

          <div className="nexus-dialog-body">
            <div className="nx-form-grid">
              <Field label="Número do Romaneio *" htmlFor="romaneio-numero">
                <Input
                  id="romaneio-numero"
                  value={form.numero}
                  onChange={(e) => setForm({ ...form, numero: e.target.value })}
                  required
                />
              </Field>
              <Field label="Responsável Emissão *" htmlFor="romaneio-responsavel-emissao">
                <Select
                  id="romaneio-responsavel-emissao"
                  value={form.responsavel_emissao_id}
                  onChange={(e) => setForm({ ...form, responsavel_emissao_id: e.target.value })}
                  required
                >
                  <option value="">Selecione</option>
                  {responsaveisValidos.map((r) => (
                    <option key={r.id} value={getUuidCandidate(r) || ''}>{r.nome}</option>
                  ))}
                </Select>
              </Field>

              <Field label="Nome do Destinatário *" htmlFor="romaneio-dest-nome" className="nx-sngpc-field--full">
                <Input
                  id="romaneio-dest-nome"
                  value={form.destinatario_nome}
                  onChange={(e) => setForm({ ...form, destinatario_nome: e.target.value })}
                  required
                />
              </Field>
              <Field label="CNPJ do Destinatário" htmlFor="romaneio-dest-cnpj">
                <Input
                  id="romaneio-dest-cnpj"
                  value={form.destinatario_cnpj}
                  onChange={(e) => setForm({ ...form, destinatario_cnpj: e.target.value })}
                  placeholder="00.000.000/0000-00"
                />
              </Field>
              <Field label="Endereço do Destinatário *" htmlFor="romaneio-dest-endereco" className="nx-sngpc-field--full">
                <Textarea
                  id="romaneio-dest-endereco"
                  rows={2}
                  value={form.destinatario_endereco}
                  onChange={(e) => setForm({ ...form, destinatario_endereco: e.target.value })}
                  required
                />
              </Field>

              <Field label="Transportadora" htmlFor="romaneio-transportadora">
                <Input
                  id="romaneio-transportadora"
                  value={form.transportadora}
                  onChange={(e) => setForm({ ...form, transportadora: e.target.value })}
                />
              </Field>
              <Field label="Motorista" htmlFor="romaneio-motorista">
                <Input
                  id="romaneio-motorista"
                  value={form.motorista}
                  onChange={(e) => setForm({ ...form, motorista: e.target.value })}
                />
              </Field>
              <Field label="Veículo (Sincronizado do ERP)" htmlFor="romaneio-veiculo" className="nx-sngpc-field--full">
                <Select
                  id="romaneio-veiculo"
                  value={form.veiculo_id}
                  onChange={(e) => setForm({ ...form, veiculo_id: e.target.value })}
                >
                  <option value="">Selecione um veículo</option>
                  {veiculosValidos.map((v) => (
                    <option key={v.id} value={getUuidCandidate(v) || ''}>{v.placa} - {v.marca} {v.modelo}</option>
                  ))}
                </Select>
              </Field>
              <Field label="Observações" htmlFor="romaneio-observacoes" className="nx-sngpc-field--full">
                <Textarea
                  id="romaneio-observacoes"
                  rows={2}
                  value={form.observacoes}
                  onChange={(e) => setForm({ ...form, observacoes: e.target.value })}
                />
              </Field>
            </div>

            <div style={{ marginTop: '1.25rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                <span className="nx-field-label">Itens do Romaneio</span>
                <Button type="button" variant="ghost" onClick={adicionarItem}>
                  <Plus size={14} strokeWidth={2.2} />
                  Adicionar Item
                </Button>
              </div>

              <div className="nx-table-wrap">
                <table className="nx-table">
                  <thead>
                    <tr>
                      <th>Produto *</th>
                      <th>Lote</th>
                      <th>Quantidade *</th>
                      <th className="nx-col-acoes">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {form.itens.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="nx-table-loading">Adicione pelo menos um item ao romaneio</td>
                      </tr>
                    ) : (
                      form.itens.map((item, index) => (
                        <tr key={index}>
                          <td>
                            <Select value={item.produto_id} onChange={(e) => onProdutoItemChange(index, e.target.value)}>
                              <option value="">Selecione</option>
                    {produtosValidos.map((p) => (
                      <option key={p.id} value={getUuidCandidate(p) || ''}>{p.codigo} - {p.descricao}</option>
                    ))}
                            </Select>
                          </td>
                          <td>
                            <Select value={item.lote_id || ''} onChange={(e) => atualizarItem(index, { lote_id: e.target.value })}>
                              <option value="">-</option>
                              {(lotesPorProduto[index] || []).filter((l) => getUuidCandidate(l)).map((l) => (
                                <option key={l.id} value={getUuidCandidate(l) || ''}>{l.numero_lote}</option>
                              ))}
                            </Select>
                          </td>
                          <td>
                            <DecimalInput
                              value={item.quantidade}
                              onValueChange={(value) => atualizarItem(index, { quantidade: value ?? 0 })}
                            />
                          </td>
                          <td className="nx-col-acoes">
                            <IconButton label="Remover" variant="danger" onClick={() => removerItem(index)}>
                              <Trash2 size={15} strokeWidth={1.9} aria-hidden />
                            </IconButton>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="ghost" disabled={salvando}>Cancelar</Button>
            </DialogClose>
            <Button type="submit" loading={salvando} loadingText="Salvando...">
              <Save size={16} /> Salvar
            </Button>
          </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={dialogDetalhes} onOpenChange={setDialogDetalhes}>
        <DialogContent className="nexus-dialog--wide">
          <DialogHeader>
            <DialogTitle>Romaneio {itemVisualizar?.numero ?? ''}</DialogTitle>
            <DialogDescription>Detalhes do romaneio e itens vinculados.</DialogDescription>
          </DialogHeader>

          {itemVisualizar && (
            <div>
              <div
                style={{
                  marginBottom: '1rem',
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, 1fr)',
                  gap: '0.75rem',
                  borderRadius: 'var(--nx-radius-sm)',
                  background: 'var(--nx-accent-soft)',
                  padding: '0.75rem',
                  fontSize: '0.85rem',
                }}
              >
                <div>
                  <span style={{ display: 'block', fontSize: '0.72rem', color: 'var(--nx-muted)' }}>Status</span>
                  <StatusBadge label={STATUS_LABELS[itemVisualizar.status]} tone={STATUS_TONES[itemVisualizar.status]} />
                </div>
                <div>
                  <span style={{ display: 'block', fontSize: '0.72rem', color: 'var(--nx-muted)' }}>Destinatário</span>
                  {itemVisualizar.destinatario_nome}
                </div>
                <div>
                  <span style={{ display: 'block', fontSize: '0.72rem', color: 'var(--nx-muted)' }}>Veículo</span>
                  {itemVisualizar.veiculo_placa || '-'}
                </div>
                <div>
                  <span style={{ display: 'block', fontSize: '0.72rem', color: 'var(--nx-muted)' }}>Motorista</span>
                  {itemVisualizar.motorista || '-'}
                </div>
                <div>
                  <span style={{ display: 'block', fontSize: '0.72rem', color: 'var(--nx-muted)' }}>Transportadora</span>
                  {itemVisualizar.transportadora || '-'}
                </div>
                <div>
                  <span style={{ display: 'block', fontSize: '0.72rem', color: 'var(--nx-muted)' }}>Data Emissão</span>
                  {formatDate(itemVisualizar.data_emissao)}
                </div>
              </div>

              <p className="nx-field-label" style={{ marginBottom: '0.5rem' }}>Itens</p>
              <div className="nx-table-wrap">
                <table className="nx-table">
                  <thead>
                    <tr>
                      <th>Produto</th>
                      <th>Lote</th>
                      <th>Quantidade</th>
                    </tr>
                  </thead>
                  <tbody>
                    {itemVisualizar.itens.map((item, idx) => (
                      <tr key={idx}>
                        <td>{item.produto_descricao || item.produto_id}</td>
                        <td>{item.lote_numero || '-'}</td>
                        <td>{item.quantidade}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="ghost">Fechar</Button>
            </DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={dialogCarregar} onOpenChange={setDialogCarregar}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Carregar Romaneio {romaneioAcao?.numero ?? ''}</DialogTitle>
            <DialogDescription>Informe o responsável pelo carregamento para confirmar a mudança de status.</DialogDescription>
          </DialogHeader>

          <Field label="Responsável pelo Carregamento *" htmlFor="romaneio-resp-carregamento">
            <Select
              id="romaneio-resp-carregamento"
              value={responsavelCarregamento}
              onChange={(e) => setResponsavelCarregamento(e.target.value)}
            >
              <option value="">Selecione</option>
              {responsaveisValidos.map((r) => (
                <option key={r.id} value={getUuidCandidate(r) || ''}>{r.nome}</option>
              ))}
            </Select>
          </Field>

          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="ghost">Cancelar</Button>
            </DialogClose>
            <Button
              type="button"
              variant="primary"
              onClick={confirmarCarregar}
              disabled={!responsavelCarregamento}
              loading={processando === romaneioAcao?.id}
              loadingText="Confirmando..."
            >
              Confirmar Carregamento
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={dialogImprimirIndisponivel} onOpenChange={setDialogImprimirIndisponivel}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Impressão</DialogTitle>
            <DialogDescription>A geração de PDF do romaneio ainda não foi implementada no backend.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button type="button" onClick={() => setDialogImprimirIndisponivel(false)}>Entendi</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
